import React from 'react';
import { gql } from 'urql';
import { AppStateUser } from '../../../client/providers/AppProvider';
import { useAuthenticatedQuery } from '../../../client/hooks';
import {
    ParentMyEventsDocument,
    ParentMyEventsQuery,
    ParentMyEventsQueryVariables,
} from '../../../generated/spin-graphql';
import { MyEventsCard } from './MyEventsCard';
import { MyUpcomingEvents } from './MyUpcomingEvents';
import { PlayerMyEventsSkeleton } from './PlayerMyEventsSkeleton';

type Props = {
    user: AppStateUser;
};

gql`
    query ParentMyEvents($id: String!, $primaryGames: [espin_game_definition_enum!]!, $hasPrimaries: Boolean!) {
        espin_user_by_pk(id: $id) {
            parent_profile {
                child_profiles(where: { user: { deleted_at: { _is_null: true } } }) {
                    ...MyEventsCardPlayer
                }
            }
        }

        admin_recommended_events: espin_event(
            limit: 2
            order_by: { event_start_date: asc }
            where: { admin_recommended: { _eq: true }, status: { _eq: published }, event_end_date: { _gte: "now()" } }
        ) {
            ...MyUpcomingEvents
        }
        recommended_events: espin_event(
            limit: 2
            order_by: { event_start_date: asc }
            where: {
                event_end_date: { _gte: "now()" }
                status: { _eq: published }
                event_games: { game: { game_definition: { _in: $primaryGames } } }
            }
        ) @include(if: $hasPrimaries) {
            ...MyUpcomingEvents
        }
        any_events: espin_event(
            limit: 2
            order_by: { event_start_date: asc }
            where: { event_end_date: { _gte: "now()" }, status: { _eq: published } }
        ) @skip(if: $hasPrimaries) {
            ...MyUpcomingEvents
        }
        registered_events: espin_event(
            order_by: { event_start_date: asc }
            where: {
                event_end_date: { _gte: "now()" }
                status: { _eq: published }
                event_registrations: {
                    _or: [
                        { user: { player_profile: { parent_profile: { user_id: { _eq: $id } } } } }
                        { team: { members: { player_profile: { parent_profile: { user_id: { _eq: $id } } } } } }
                    ]
                }
            }
        ) {
            ...MyEventsCard
        }
    }
`;

export const ParentMyEvents: React.FC<Props> = ({ user }) => {
    const primaryGames = user.parent_profile?.child_profiles
        .map((c) => {
            const pGame = c.player_games.find((pg) => pg.primary)?.game.game_definition;
            if (pGame) {
                return pGame;
            }
            return null;
        })
        .filter((g) => !!!g);

    // const primaryGames = childProfile?.player_games.find((g) => g.primary === true)?.game.game_definition;
    const { isReady, data } = useAuthenticatedQuery<ParentMyEventsQuery, ParentMyEventsQueryVariables>({
        query: ParentMyEventsDocument,
        variables: {
            id: user.id,
            primaryGames,
            hasPrimaries: primaryGames?.length > 0 ?? false,
        },
    });

    if (!isReady) {
        return <PlayerMyEventsSkeleton />;
    }

    const {
        recommended_events,
        registered_events,
        any_events,
        espin_user_by_pk: {
            parent_profile: { child_profiles },
        },
    } = data;

    return (
        <div className="parent-my-events">
            <div className="my-players-events">
                {child_profiles.map((profile, index) => (
                    <MyEventsCard key={`my-events-card-${index}`} events={registered_events} player={profile} />
                ))}
            </div>
            <MyUpcomingEvents events={recommended_events ?? any_events} />
        </div>
    );
};
